import { Brand, MenulyticsReport } from '@vgrubs/components';
import { DateTime } from 'luxon';

/** What a row shows, derived once so the row and the share modal agree. */
export type ReportPhase = 'generating' | 'ready' | 'expired' | 'failed';

export const reportPhase = (
  report: MenulyticsReport,
  now = Date.now()
): ReportPhase => {
  if (report.status === 'failed') return 'failed';
  if (report.status !== 'completed') return 'generating';
  const expiresAt = report.reportExpiresAt
    ? Date.parse(report.reportExpiresAt)
    : NaN;
  return expiresAt <= now ? 'expired' : 'ready';
};

export const isPending = (report: MenulyticsReport) =>
  report.status === 'queued' || report.status === 'processing';

/**
 * `Sep 9 - Oct 8, 2026`. The days are calendar days of the location, so they
 * are read as plain dates and never shifted into the viewer's zone.
 */
export const formatReportPeriod = ({
  periodStart,
  periodEnd,
}: MenulyticsReport) => {
  const start = DateTime.fromISO(periodStart);
  const end = DateTime.fromISO(periodEnd);
  if (!start.isValid || !end.isValid) return `${periodStart} - ${periodEnd}`;
  const startFormat = start.year === end.year ? 'LLL d' : 'LLL d, yyyy';
  return `${start.toFormat(startFormat)} - ${end.toFormat('LLL d, yyyy')}`;
};

/** `Oct 12, 2026` */
export const formatReportDay = (iso: string | null) => {
  const date = iso ? DateTime.fromISO(iso) : null;
  return date?.isValid ? date.toFormat('LLL d, yyyy') : '-';
};

/** `Oct 12, 2026, 3:04 PM` */
export const formatReportMoment = (iso: string | null) => {
  const date = iso ? DateTime.fromISO(iso) : null;
  return date?.isValid ? date.toFormat('LLL d, yyyy, h:mm a') : '-';
};

/**
 * Whether a report row belongs to a brand. The location brand can be stored
 * under its own id or under 0, the id every menu route knows it by.
 */
export const reportIsForBrand = (
  report: MenulyticsReport,
  brand: Pick<Brand, 'id' | 'isLocation'>
) => report.brandId === brand.id || (brand.isLocation && report.brandId === 0);

/**
 * The brand name of a row on the location screen. The location brand is
 * stored under its real id, which that screen does not have, so a row that is
 * none of the virtual brands is the location's own.
 */
export const reportBrandName = (
  report: MenulyticsReport,
  brands: Pick<Brand, 'id' | 'name'>[],
  locationName: string
) => brands.find((brand) => brand.id === report.brandId)?.name ?? locationName;
